import { useMutation } from "@apollo/client";
import {
  ArchiveContractorResponse,
  ArchiveLaborMeasurementResponse,
  ArchiveLaborResponse,
  ArchiveMaterialCategoryResponse,
  ArchiveMaterialMeasurementResponse,
  ArchiveMaterialResponse,
  ArchiveMiscellaneousFieldResponse,
  ArchiveProductSystemResponse,
  CreateAccountResponse,
  CreateContractorChangeOrderResponse,
  CreateContractorInput,
  CreateContractorOrderInput,
  CreateContractorReportInput,
  CreateContractorReportResponse,
  CreateContractorResponse,
  CreateCustomerInput,
  CreateLaborInput,
  CreateLaborMeasurementInput,
  CreateLaborMeasurementResponse,
  CreateLaborResponse,
  CreateLeadInput,
  CreateMaterialCategoryInput,
  CreateMaterialCategoryResponse,
  CreateMaterialInput,
  CreateMaterialMeasurementInput,
  CreateMaterialMeasurementResponse,
  CreateMaterialResponse,
  CreateMiscFieldInput,
  CreateMiscFieldResponse,
  CreateNoteInput,
  CreateNotesResponse,
  CreateProductSystemInput,
  CreateProductSystemResponse,
  DeleteNoteResponse,
  EditNoteInput,
  EditNotesResponse,
  LeadResponses,
  UpdateContractorInput,
  UpdateContractorResponse,
  UpdateLaborInput,
  UpdateLaborMeasurementInput,
  UpdateLaborMeasurementResponse,
  UpdateLaborResponse,
  UpdateMaterialCategoryInput,
  UpdateMaterialCategoryResponse,
  UpdateMaterialInput,
  UpdateMaterialMeasurementInput,
  UpdateMaterialMeasurementResponse,
  UpdateMaterialResponse,
  PaymentMethodUpdateInput,
  UpdatePaymentMethodResponse,
  UpdateProductInput,
  UpdateProductSystemInput,
  UpdateProductSystemResponse,
} from "../../../types/graphQlQuery.interface";
import {
  ARCHIVE_CONTRACTOR,
  ARCHIVE_LABOR,
  ARCHIVE_LABOR_MEASUREMENT,
  ARCHIVE_MATERIAL,
  ARCHIVE_MATERIAL_CATEGORY,
  ARCHIVE_MATERIAL_MEASUREMENT,
  ARCHIVE_MISC_FIELD,
  ARCHIVE_PRODUCT_SYSTEM_MUTATION,
  CREATE_ACCOUNT,
  CREATE_CONTRACTOR,
  CREATE_CONTRACTOR_ORDER,
  CREATE_CONTRACTOR_REPORT,
  CREATE_LABOR,
  CREATE_LABOR_MEASUREMENT,
  CREATE_LEAD,
  CREATE_MATERIAL,
  CREATE_MATERIAL_CATEGORY,
  CREATE_MATERIAL_MEASUREMENT,
  CREATE_MISC_FIELD,
  CREATE_NOTES_MUTATION,
  CREATE_PRODUCT_SYSTEM_MUTATION,
  CREATE_TAG,
  DELETE_NOTES_MUTATION,
  DELETE_TAG,
  EDIT_NOTES_MUTATION,
  UPDATE_CONTRACTOR,
  UPDATE_LABOR,
  UPDATE_LABOR_MEASUREMENT,
  UPDATE_MATERIAL,
  UPDATE_MATERIAL_CATEGORY,
  UPDATE_MATERIAL_MEASUREMENT,
  UPDATE_PRODUCT_SYSTEM_MUTATION,
  UPDATE_MATERIAL_MEASUREMENT_ORDER,
  UPDATE_LABOR_MEASUREMENT_ORDER,
  UPDATE_PAYMENT_METHOD,
  UPDATE_TAG,
  EDIT_PRODUCT_SYSTEM,
  UPDATE_SUPPLIER,
  ARCHIVE_SUPPLIER,
  CREATE_SUPPLIER,
  DELETE_LABOR_VARIANT,
} from "../../operation/mutations";
import { SupplierInput, SupplierUpdateInput } from "../../../types/ItemsCreation";

export const useCreateNotes = () => {
  const [createNotesMutation] = useMutation<
    CreateNotesResponse,
    { input: CreateNoteInput }
  >(CREATE_NOTES_MUTATION);

  const createNotes = async (input: CreateNoteInput) => {
    try {
      const { data } = await createNotesMutation({
        variables: { input },
      });
      return data?.CreateNote ?? false;
    } catch (error) {
      console.error("Error creating Notes:", error);
      return false;
    }
  };

  return { createNotes };
};

export const useEditNotes = () => {
  const [editNotesMutation] = useMutation<
    EditNotesResponse,
    { input: EditNoteInput }
  >(EDIT_NOTES_MUTATION);

  const editNotes = async (input: EditNoteInput) => {
    try {
      const { data } = await editNotesMutation({
        variables: { input },
      });
      return data?.EditNote ?? false;
    } catch (error) {
      console.error("Error editing Notes:", error);
      return false;
    }
  };

  return { editNotes };
};

export const useDeleteNotes = () => {
  const [deleteNotesMutation] = useMutation<DeleteNoteResponse, { id: number }>(
    DELETE_NOTES_MUTATION
  );

  const deleteNote = async (id: number) => {
    try {
      const { data } = await deleteNotesMutation({
        variables: { id },
      });
      return data?.DeleteNote ?? false;
    } catch (error) {
      console.error("Error deleting Notes:", error);
      return false;
    }
  };

  return { deleteNote };
};

export const useCreateProductSystem = () => {
  const [createProductSystemMutation] = useMutation<
    CreateProductSystemResponse,
    { input: CreateProductSystemInput }
  >(CREATE_PRODUCT_SYSTEM_MUTATION);

  const createProductSystem = async (input: CreateProductSystemInput) => {
    try {
      const { data } = await createProductSystemMutation({
        variables: { input },
      });
      return data?.createProductSystem ?? false;
    } catch (error) {
      console.error("Error creating product system:", error);
      return false;
    }
  };

  return { createProductSystem };
};

export const useUpdateProductSystem = () => {
  const [updateProductSystemMutation] = useMutation<
    UpdateProductSystemResponse,
    { input: UpdateProductInput }
  >(UPDATE_PRODUCT_SYSTEM_MUTATION);

  const updateProductSystem = async (input: UpdateProductInput) => {
    try {
      const { data } = await updateProductSystemMutation({
        variables: { input },
      });
      return data?.updateProduct ?? false;
    } catch (error) {
      console.error("Error updating product system:", error);
      return false;
    }
  };

  return { updateProductSystem };
};

export const useCreateMaterialCategory = () => {
  const [createMaterialCategoryMutation] = useMutation<
    CreateMaterialCategoryResponse,
    { input: CreateMaterialCategoryInput }
  >(CREATE_MATERIAL_CATEGORY);

  const createMaterialCategory = async (input: CreateMaterialCategoryInput) => {
    try {
      const { data } = await createMaterialCategoryMutation({
        variables: { input },
      });
      return data?.createMaterialCategory ?? false;
    } catch (error) {
      console.error("Error creating material category:", error);
      return false;
    }
  };

  return { createMaterialCategory };
};

export const useUpdateMaterialCategory = () => {
  const [updateMaterialCategoryMutation] = useMutation<
    UpdateMaterialCategoryResponse,
    { input: UpdateMaterialCategoryInput }
  >(UPDATE_MATERIAL_CATEGORY);

  const updateMaterialCategory = async (input: UpdateMaterialCategoryInput) => {
    try {
      const { data } = await updateMaterialCategoryMutation({
        variables: { input },
      });
      return data?.updateMaterialCategory ?? false;
    } catch (error) {
      console.error("Error updating material category:", error);
      return false;
    }
  };

  return { updateMaterialCategory };
};

export const useCreateMiscField = () => {
  const [createMiscField] = useMutation<
    CreateMiscFieldResponse,
    { input: CreateMiscFieldInput }
  >(CREATE_MISC_FIELD);

  const createMiscFields = async (input: CreateMiscFieldInput) => {
    try {
      const { data } = await createMiscField({
        variables: { input },
      });
      return data?.createMiscField ?? false;
    } catch (error) {
      console.error("Error creating misc field:", error);
      return false;
    }
  };

  return { createMiscFields };
};

export const useCreateLaborMeasurement = () => {
  const [createLaborMeasurementMutation] = useMutation<
    CreateLaborMeasurementResponse,
    { input: CreateLaborMeasurementInput }
  >(CREATE_LABOR_MEASUREMENT);

  const createLaborMeasurement = async (input: CreateLaborMeasurementInput) => {
    try {
      const { data } = await createLaborMeasurementMutation({
        variables: { input },
      });
      return data?.createLaborMeasurement ?? false;
    } catch (error) {
      console.error("Error creating labor measurement:", error);
      return false;
    }
  };

  return { createLaborMeasurement };
};

export const useUpdateLaborMeasurement = () => {
  const [updateLaborMeasurementMutation] = useMutation<
    UpdateLaborMeasurementResponse,
    { input: UpdateLaborMeasurementInput }
  >(UPDATE_LABOR_MEASUREMENT);

  const updateLaborMeasurement = async (input: UpdateLaborMeasurementInput) => {
    try {
      const { data } = await updateLaborMeasurementMutation({
        variables: { input },
      });
      return data?.updateLaborMeasurement ?? false;
    } catch (error) {
      console.error("Error updating labor measurement:", error);
      return false;
    }
  };

  return { updateLaborMeasurement };
};

export const useCreateMaterialMeasurement = () => {
  const [createMaterialMeasurementMutation] = useMutation<
    CreateMaterialMeasurementResponse,
    { input: CreateMaterialMeasurementInput }
  >(CREATE_MATERIAL_MEASUREMENT);

  const createMaterialMeasurement = async (
    input: CreateMaterialMeasurementInput
  ) => {
    try {
      const { data } = await createMaterialMeasurementMutation({
        variables: { input },
      });
      return data?.createMaterialMeasurement ?? false;
    } catch (error) {
      console.error("Error creating material measurement:", error);
      return false;
    }
  };

  return { createMaterialMeasurement };
};

export const useUpdateMaterialMeasurement = () => {
  const [updateMaterialMeasurementMutation] = useMutation<
    UpdateMaterialMeasurementResponse,
    { input: UpdateMaterialMeasurementInput }
  >(UPDATE_MATERIAL_MEASUREMENT);

  const updateMaterialMeasurement = async (
    input: UpdateMaterialMeasurementInput
  ) => {
    try {
      const { data } = await updateMaterialMeasurementMutation({
        variables: { input },
      });
      return data?.updateMaterialMeasurement ?? false;
    } catch (error) {
      console.error("Error updating material measurement:", error);
      return false;
    }
  };

  return { updateMaterialMeasurement };
};

export const useUpdateMaterialMeasurementOrder = () => {
  const [updateMaterialMeasurementOrderMutation] = useMutation<
    boolean,
    { newOrder: number[] }
  >(UPDATE_MATERIAL_MEASUREMENT_ORDER);

  const updateMaterialMeasurementOrder = async (newOrder: number[]) => {
    try {
      const { data } = await updateMaterialMeasurementOrderMutation({
        variables: { newOrder },
      });
      return data ?? false;
    } catch (error) {
      console.error("Error updating material measurement order:", error);
      return false;
    }
  };

  return { updateMaterialMeasurementOrder };
};

export const useUpdateLaborMeasurementOrder = () => {
  const [updateLaborMeasurementOrderMutation] = useMutation<
    boolean,
    { newOrder: number[] }
  >(UPDATE_LABOR_MEASUREMENT_ORDER);
  const updateLaborMeasurementOrder = async (newOrder: number[]) => {
    try {
      const { data } = await updateLaborMeasurementOrderMutation({
        variables: { newOrder },
      });
      return data ?? false;
    } catch (error) {
      console.error("Error updating labor measurement order:", error);
      return false;
    }
  }
  return { updateLaborMeasurementOrder };
};

export const useCreateMaterial = () => {
  const [createMaterialMutation] = useMutation<
    CreateMaterialResponse,
    { input: CreateMaterialInput }
  >(CREATE_MATERIAL);

  const createMaterial = async (input: CreateMaterialInput) => {
    try {
      const { data } = await createMaterialMutation({
        variables: { input },
      });
      return data?.createMaterial ?? false;
    } catch (error) {
      console.error("Error creating material:", error);
      return false;
    }
  };

  return { createMaterial };
};

export const useUpdateMaterial = () => {
  const [updateMaterialMutation] = useMutation<
    UpdateMaterialResponse,
    { input: UpdateMaterialInput }
  >(UPDATE_MATERIAL);

  const updateMaterial = async (input: UpdateMaterialInput) => {
    try {
      const { data } = await updateMaterialMutation({
        variables: { input },
      });
      return data?.updateMaterial ?? false;
    } catch (error) {
      console.error("Error updating material:", error);
      return false;
    }
  };

  return { updateMaterial };
};

export const useCreateContractorReports = () => {
  const [createContractorReportsMutation] = useMutation<
    CreateContractorReportResponse,
    { input: CreateContractorReportInput }
  >(CREATE_CONTRACTOR_REPORT);

  const createContractorReport = async (input: CreateContractorReportInput) => {
    try {
      const { data } = await createContractorReportsMutation({
        variables: { input },
      });
      return data?.createContractorReport ?? false;
    } catch (error) {
      console.error("Error creating material:", error);
      return false;
    }
  };

  return { createContractorReport };
};

export const useCreateContractor = () => {
  const [createContractorMutation] = useMutation<
    CreateContractorResponse,
    { input: CreateContractorInput }
  >(CREATE_CONTRACTOR);

  const createContractor = async (input: CreateContractorInput) => {
    try {
      const { data } = await createContractorMutation({
        variables: { input },
      });
      return data?.createContractor ?? false;
    } catch (error) {
      console.error("Error creating material:", error);
      return false;
    }
  };

  return { createContractor };
};

export const useUpdateContractor = () => {
  const [updateContractorMutation] = useMutation<
    UpdateContractorResponse,
    { input: UpdateContractorInput }
  >(UPDATE_CONTRACTOR);
  const updateContractor = async (input: UpdateContractorInput) => {
    try {
      const { data } = await updateContractorMutation({
        variables: { input },
      });
      return data?.updateContractor ?? false;
    } catch (error) {
      console.error("Error updating material:", error);
      return false;
    }
  }
  return { updateContractor };
}

export const useCreateLabor = () => {
  const [createLaborMutation] = useMutation<
    CreateLaborResponse,
    { input: CreateLaborInput }
  >(CREATE_LABOR);

  const createLabor = async (input: CreateLaborInput) => {
    try {
      const { data } = await createLaborMutation({
        variables: { input },
      });
      return data?.createLabor ?? false;
    } catch (error) {
      console.error("Error creating labor:", error);
      return false;
    }
  };

  return { createLabor };
};

export const useUpdateLabor = () => {
  const [updateLaborMutation] = useMutation<
    UpdateLaborResponse,
    { input: UpdateLaborInput }
  >(UPDATE_LABOR);

  const updateLabor = async (input: UpdateLaborInput) => {
    try {
      const { data } = await updateLaborMutation({
        variables: { input },
      });
      return data?.updateLabor ?? false;
    } catch (error) {
      console.error("Error updating labor:", error);
      return false;
    }
  };

  return { updateLabor };
};

export const useCreateContractorChangeOrder = () => {
  const [createContractorChangeOrderMutation] = useMutation<
    CreateContractorChangeOrderResponse,
    { input: CreateContractorOrderInput }
  >(CREATE_CONTRACTOR_ORDER);

  const createContractorChangeOrder = async (
    input: CreateContractorOrderInput
  ) => {
    try {
      const { data } = await createContractorChangeOrderMutation({
        variables: { input },
      });
      return data?.createContractorOrder ?? false;
    } catch (error) {
      console.error("Error creating contractor order:", error);
      return false;
    }
  };

  return { createContractorChangeOrder };
};

export const useArchiveProductSystem = () => {
  const [archiveProductSystemMutation] = useMutation<
    ArchiveProductSystemResponse,
    { productSystemId: number }
  >(ARCHIVE_PRODUCT_SYSTEM_MUTATION);

  const archiveProductSystem = async ( productSystemId: number ) => {
    try {
      const { data } = await archiveProductSystemMutation({
        variables: { productSystemId },
      });
      return data?.ToggleArchiveStatusProduct ?? false;
    } catch (error) {
      console.error("Error Publishing product system:", error);
      return false;
    }
  };

  return { archiveProductSystem };
};

export const useArchiveMaterialCategory = () => {
  const [archiveMaterialCategoryMutation] = useMutation<
    ArchiveMaterialCategoryResponse,
    { materialCategoryId: number }
  >(ARCHIVE_MATERIAL_CATEGORY);

  const archiveMaterialCategory = async ( materialCategoryId: number ) => {
    try {
      const { data } = await archiveMaterialCategoryMutation({
        variables: { materialCategoryId },
      });
      return data?.ArchiveMaterialCategory ?? false;
    } catch (error) {
      console.error("Error Archiving material category:", error);
      return false;
    }
  };

  return { archiveMaterialCategory };
};


export const useArchiveMaterialMeasurement = () => {
  const [archiveMaterialMeasurementMutation] = useMutation<
    ArchiveMaterialMeasurementResponse,
    { materialMeasurementId: number }
  >(ARCHIVE_MATERIAL_MEASUREMENT);

  const archiveMaterialMeasurement = async ( materialMeasurementId: number ) => {
    try {
      const { data } = await archiveMaterialMeasurementMutation({
        variables: { materialMeasurementId },
      });
      return data?.ArchiveMaterialMeasurement ?? false;
    } catch (error) {
      console.error("Error Archiving material measurement:", error);
      return false;
    }
  };

  return { archiveMaterialMeasurement };
}

export const useArchiveMaterial = () => {
  const [archiveMaterialMutation] = useMutation<
    ArchiveMaterialResponse,
    { materialId: number }
  >(ARCHIVE_MATERIAL);

  const archiveMaterial = async ( materialId: number ) => {
    try {
      const { data } = await archiveMaterialMutation({
        variables: { materialId },
      });
      return data?.ArchiveMaterial ?? false;
    } catch (error) {
      console.error("Error Archiving material:", error);
      return false;
    }
  };

  return { archiveMaterial };
};

export const useArchiveLaborMeasurement = () => {
  const [archiveLaborMeasurementMutation] = useMutation<
    ArchiveLaborMeasurementResponse,
    { laborMeasurementId: number }
  >(ARCHIVE_LABOR_MEASUREMENT);

  const archiveLaborMeasurement = async ( laborMeasurementId: number ) => {
    try {
      const { data } = await archiveLaborMeasurementMutation({
        variables: { laborMeasurementId },
      });
      return data?.ArchiveLaborMeasurement ?? false;
    } catch (error) {
      console.error("Error Archiving labor measurement:", error);
      return false;
    }
  };

  return { archiveLaborMeasurement };
};

export const useArchiveLabor = () => {
  const [archiveLaborMutation] = useMutation<
    ArchiveLaborResponse,
    { laborId: number }
  >(ARCHIVE_LABOR);

  const archiveLabor = async ( laborId: number ) => {
    try {
      const { data } = await archiveLaborMutation({
        variables: { laborId },
      });
      return data?.ArchiveLabor ?? false;
    } catch (error) {
      console.error("Error Archiving labor:", error);
      return false;
    }
  };

  return { archiveLabor };
};

export const useArchiveContractor = () => {
  const [archiveContractorMutation] = useMutation<
    ArchiveContractorResponse,
    { contractorId: number }
  >(ARCHIVE_CONTRACTOR);

  const archiveContractor = async ( contractorId: number ) => {
    try {
      const { data } = await archiveContractorMutation({
        variables: { contractorId },
      });
      return data?.ArchiveContractor ?? false;
    } catch (error) {
      console.error("Error Archiving contractor:", error);
      return false;
    }
  };

  return { archiveContractor };
};

export const useArchiveMiscField = () => {
  const [archiveMiscField] = useMutation<
    ArchiveMiscellaneousFieldResponse,
    { mescFieldId: number }
  >(ARCHIVE_MISC_FIELD);
  const archiveMiscFields = async ( mescFieldId: number ) => {
    try {
      const { data } = await archiveMiscField({
        variables: { mescFieldId },
      });
      return data?.ArchiveMiscellaneousField ?? false;
    } catch (error) {
      console.error("Error Archiving misc field:", error);
      return false;
    }
  };
  return { archiveMiscFields };
}

export const useCreateLead = () => {
  const [createLeadMutation] = useMutation<
    { createLead: LeadResponses },
    { input: CreateLeadInput }
  >(CREATE_LEAD);

  const CreateLead = async (input: CreateLeadInput): Promise<string> => {
    try {
      const { data } = await createLeadMutation({
        variables: { input },
      });
      if (!data?.createLead.leadId) {
        throw new Error("Failed to create lead: Lead ID is not available");
      }
      return data.createLead.leadId;
    } catch (error) {
      console.error("Error creating lead:", error);
      throw error;
    }
  };

  return { CreateLead };
};

export const useCreateAccount = () => {
  const [createAccountMutation] = useMutation<
    { createAccount: CreateAccountResponse },
    { input: CreateCustomerInput }
  >(CREATE_ACCOUNT);

  const createAccount = async (input: CreateCustomerInput): Promise<CreateAccountResponse> => {
    try {
      const { data } = await createAccountMutation({
        variables: { input },
      });
      if (!data?.createAccount.token) {
        throw new Error("Failed to create account: Token is not available");
      }
      return {
        token: data.createAccount.token,
        projectId: data.createAccount.projectId,
        projectRefNumber: data.createAccount.projectRefNumber,
      };
    } catch (error) {
      console.error("Error creating account:", error);
      throw error;
    }
  };

  return { createAccount };
};


export const useEditProductSystem = () => {
  const [editProductSystemMutation, { loading, error }] = useMutation<
    { updateProductSystem: boolean },
    { input: UpdateProductSystemInput }
  >(EDIT_PRODUCT_SYSTEM);

  const editProductSystem = async (
    input: UpdateProductSystemInput
  ): Promise<boolean> => {
    try {
      const { data } = await editProductSystemMutation({
        variables: { input },
      });
      if (data?.updateProductSystem === undefined) {
        throw new Error(
          "Failed to update product system: Product system data is not available"
        );
      }
      return data.updateProductSystem;
    } catch (error) {
      console.error("Error updating product system:", error);
      throw error;
    }
  };

  return { editProductSystem, loading, error };
};

export const useEditPaymentMode = () => {
  const [updatePaymentMethodMutation, { loading, error }] = useMutation<
    UpdatePaymentMethodResponse,
    { input: PaymentMethodUpdateInput }
  >(UPDATE_PAYMENT_METHOD);

  const editPaymentMode = async (input: PaymentMethodUpdateInput): Promise<boolean> => {
    try {
      const { data } = await updatePaymentMethodMutation({
        variables: { input },
      });

      if (!data) {
        throw new Error("No data returned from update payment method mutation");
      }

      return data.updatePaymentMethod;
    } catch (error) {
      console.error("Error updating payment method:", error);
      throw error;
    }
  };

  return { editPaymentMode, loading, error };
};

export const useCreateTag = () => {
  const [createTagMutation] = useMutation(CREATE_TAG);

  const createTag = async (tag: string): Promise<boolean> => {
    try {
      const { data } = await createTagMutation({
        variables: { tag },
      });
      return !!data;
    } catch (error) {
      console.error("Error creating tag:", error);
      return false;
    }
  };

  return { createTag };
};

export const useUpdateTag = () => {
  const [updateTagMutation] = useMutation(UPDATE_TAG);

  const updateTag = async (id: number, name: string): Promise<boolean> => {
    try {
      const { data } = await updateTagMutation({
        variables: { id, name },
      });
      return !!data;
    } catch (error) {
      console.error("Error updating tag:", error);
      return false;
    }
  };

  return { updateTag };
};

export const useDeleteTag = () => {
  const [deleteTagMutation] = useMutation(DELETE_TAG);

  const deleteTag = async (id: number): Promise<boolean> => {
    try {
      const { data } = await deleteTagMutation({
        variables: { id },
      });
      return !!data;
    } catch (error) {
      console.error("Error deleting tag:", error);
      return false;
    }
  };

  return { deleteTag };
};

export const useCreateSupplier = () => {
  const [createSupplier, { loading, error }] = useMutation(CREATE_SUPPLIER);
  return {
    createSupplier: (input: SupplierInput) => createSupplier({ variables: { input } }),
    loading,
    error,
  };
};

export const useUpdateSupplier = () => {
  const [updateSupplier, { loading, error }] = useMutation(UPDATE_SUPPLIER);
  return {
    updateSupplier: (input: SupplierUpdateInput) => updateSupplier({ variables: { input } }),
    loading,
    error,
  };
};

export const useArchiveSupplier = () => {
  const [archiveSupplier, { loading, error }] = useMutation(ARCHIVE_SUPPLIER);
  return {
    archiveSupplier: (id: number) => archiveSupplier({ variables: { id } }),
    loading,
    error,
  };
};

export const useDeleteLaborVariant = () => {
  const [deleteLaborVariantMutation] = useMutation<
    { DeleteLaborVariant: boolean },
    { id: number }
  >(DELETE_LABOR_VARIANT);

  const deleteLaborVariant = async (id: number) => {
    try {
      const { data } = await deleteLaborVariantMutation({
        variables: { id },
      });
      return data?.DeleteLaborVariant ?? false;
    } catch (error) {
      console.error("Error deleting labor variant:", error);
      return false;
    }
  };

  return { deleteLaborVariant };
};